import type { CupertinoIconName } from '../icons/Icon';

// AppDestinations registry (lib/ui/navigation/app_destinations.dart, spec 01 §7.1).

export type TabId = 'home' | 'library' | 'downloads' | 'settings' | 'menu';
export type OverlayId = 'statistics' | 'reading-list' | 'connections';

export interface Destination<Id extends string = string> {
  id: Id;
  /** Verbatim Flutter label (tooltip, accessible name, Menu row text). */
  label: string;
  icon: CupertinoIconName;
  selectedIcon: CupertinoIconName;
  path: string;
}

export const TABS: readonly Destination<TabId>[] = [
  { id: 'home', label: 'Home', icon: 'house', selectedIcon: 'house_fill', path: '/' },
  { id: 'library', label: 'Library', icon: 'book', selectedIcon: 'book_fill', path: '/library' },
  { id: 'downloads', label: 'Downloads', icon: 'arrow_down_circle', selectedIcon: 'arrow_down_circle_fill', path: '/downloads' },
  { id: 'settings', label: 'Settings', icon: 'gear', selectedIcon: 'gear_solid', path: '/settings' },
  { id: 'menu', label: 'Menu', icon: 'line_horizontal_3', selectedIcon: 'line_horizontal_3', path: '/menu' },
];

export const OVERLAYS: readonly Destination<OverlayId>[] = [
  { id: 'statistics', label: 'Statistics', icon: 'chart_bar', selectedIcon: 'chart_bar_fill', path: '/menu/statistics' },
  { id: 'reading-list', label: 'Reading List', icon: 'eyeglasses', selectedIcon: 'eyeglasses', path: '/menu/reading-list' },
  { id: 'connections', label: 'Connections', icon: 'link_circle', selectedIcon: 'link_circle_fill', path: '/menu/connections' },
];

export const TAB_IDS: readonly TabId[] = TABS.map((t) => t.id);

/** Flutter `moreMenuItems`: the Menu tab rows, in order. */
export const MENU_DESTINATIONS: readonly Destination<TabId | OverlayId>[] = [
  ...TABS.filter((t) => t.id !== 'menu'),
  OVERLAYS[1] as Destination<OverlayId>,
  OVERLAYS[0] as Destination<OverlayId>,
  OVERLAYS[2] as Destination<OverlayId>,
];

export function tabIndex(id: TabId): number {
  return TAB_IDS.indexOf(id);
}

export function tabDestination(id: TabId): Destination<TabId> {
  return TABS.find((t) => t.id === id) as Destination<TabId>;
}

export function overlayDestination(id: OverlayId): Destination<OverlayId> {
  return OVERLAYS.find((o) => o.id === id) as Destination<OverlayId>;
}

export function tabPath(id: TabId): string {
  return tabDestination(id).path;
}

export function overlayPath(id: OverlayId): string {
  return `/menu/${id}`;
}

export function isTabId(value: unknown): value is TabId {
  return typeof value === 'string' && (TAB_IDS as readonly string[]).includes(value);
}

export function isOverlayId(value: unknown): value is OverlayId {
  return value === 'statistics' || value === 'reading-list' || value === 'connections';
}
